import { formatCatalogList, formatCatalogValue, NO_DESIRED_CHANGES_CONSULTANT_LABEL } from "./careerIntentCatalog";

export type CareerPlanGoal = {
  id: string;
  additionalContext?: string | null;
  archivedAt?: string | null;
  clarity?: number | null;
  createdAt: string;
  facilityTypes: string[];
  hasNoDesiredChanges: boolean;
  improvementAreas: string[];
  locations: string[];
  motivations: string[];
  retirementType?: string | null;
  targetPositions: string[];
  timeframe?: string | null;
  updatedAt: string;
};

export type CareerPlanCompetencyScore = {
  competencyId: string;
  competencyName: string;
  score?: number | null;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  isComplete: boolean;
};

export type CareerPlanScoreSnapshot = {
  score: number;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  source: string;
  createdAt: string;
  competency: {
    id: string;
    name: string;
  };
};

export type CareerPlanAssessmentAnswer = {
  competencyId: string;
  competencyName: string;
  questionId: string;
  questionContent: string;
  optionId: string;
  optionContent: string;
};

export type CareerPlanAssessment = {
  id: string;
  answeredQuestionCount: number;
  totalQuestionCount: number;
  updatedAt: string;
  assessmentComparisonRole?: string | null;
  scores?: CareerPlanCompetencyScore[];
  answers?: CareerPlanAssessmentAnswer[];
  competencyScoreSnapshots?: CareerPlanScoreSnapshot[];
};

export type CareerPlanHistoryRow = {
  id: string;
  dayKey: string;
  isLatest: boolean;
  itemsChanged: string[];
  itemsChangedLabel: string;
  nextRole: string | null;
  timeframe: string | null;
  clarity: string | null;
  indexCompletionPercent: number | null;
  lastUpdated: string;
  goals: CareerPlanGoal[];
};

export const LATEST_DAY_KEY = "latest";
export const ITEM_CAREER_GOALS = "Career Goals";
export const ITEM_EXPERIENCE_INDEX = "Experience Index";

export function toUtcDateKey(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString().slice(0, 10);
}

function endOfUtcDay(dayKey: string): Date {
  return new Date(`${dayKey}T23:59:59.999Z`);
}

export function formatClarity(clarity: number | null | undefined): string | null {
  if (clarity == null || Number.isNaN(clarity)) return null;
  return formatCatalogValue("clarity", clarity) || null;
}

export function getNextRoleLabel(goal: CareerPlanGoal | null | undefined): string | null {
  if (!goal) return null;

  if (goal.hasNoDesiredChanges) {
    return NO_DESIRED_CHANGES_CONSULTANT_LABEL;
  }

  const positions = formatCatalogList("targetPosition", goal.targetPositions);
  if (positions) return positions;

  if (goal.retirementType) {
    return formatCatalogValue("retirementType", goal.retirementType);
  }

  return null;
}

/**
 * Current Career Intent identity is absence of `archivedAt` only
 * (Syd/BE: archived_at set on historical versions; missing/null = current).
 */
export function isCurrentCareerIntent(goal: Pick<CareerPlanGoal, "id" | "archivedAt"> | null | undefined): boolean {
  return !!goal?.id && goal.archivedAt == null;
}

export function getCurrentCareerIntent(
  careerIntent: CareerPlanGoal | null | undefined,
  careerIntents: CareerPlanGoal[] | null | undefined
): CareerPlanGoal | null {
  if (careerIntent && isCurrentCareerIntent(careerIntent)) return careerIntent;
  return (careerIntents ?? []).find((goal) => isCurrentCareerIntent(goal)) ?? null;
}

function formatGoalTimeframe(goal: CareerPlanGoal | null | undefined): string | null {
  if (!goal?.timeframe) return null;
  return formatCatalogValue("timeframe", goal.timeframe);
}

export function goalsAsOfDay(goals: CareerPlanGoal[], dayKey: string): CareerPlanGoal[] {
  if (dayKey === LATEST_DAY_KEY) {
    return goals.filter((goal) => !goal.archivedAt).sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  }

  const end = endOfUtcDay(dayKey);
  return goals
    .filter((goal) => {
      const created = new Date(goal.createdAt);
      if (Number.isNaN(created.getTime()) || created > end) return false;
      if (!goal.archivedAt) return true;
      const archived = new Date(goal.archivedAt);
      return !Number.isNaN(archived.getTime()) && archived > end;
    })
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

export function primaryGoal(goals: CareerPlanGoal[]): CareerPlanGoal | null {
  return goals[0] || null;
}

export function indexCompletionPercent(answered: number, total: number): number | null {
  if (total <= 0) return null;
  return Math.round((answered / total) * 100);
}

export function eiScoresAsOfDay(
  snapshots: CareerPlanScoreSnapshot[],
  dayKey: string
): Array<{
  competencyId: string;
  competencyName: string;
  score: number;
  answeredQuestionCount: number;
  totalQuestionCount: number;
}> {
  const end = dayKey === LATEST_DAY_KEY ? new Date() : endOfUtcDay(dayKey);
  const latestByCompetency = new Map<
    string,
    {
      competencyId: string;
      competencyName: string;
      score: number;
      answeredQuestionCount: number;
      totalQuestionCount: number;
      createdAt: string;
    }
  >();

  for (const snapshot of snapshots) {
    const created = new Date(snapshot.createdAt);
    if (Number.isNaN(created.getTime()) || created > end) continue;
    const existing = latestByCompetency.get(snapshot.competency.id);
    if (!existing || new Date(existing.createdAt) < created) {
      latestByCompetency.set(snapshot.competency.id, {
        competencyId: snapshot.competency.id,
        competencyName: snapshot.competency.name,
        score: snapshot.score,
        answeredQuestionCount: snapshot.answeredQuestionCount,
        totalQuestionCount: snapshot.totalQuestionCount,
        createdAt: snapshot.createdAt
      });
    }
  }

  return [...latestByCompetency.values()]
    .map(({ createdAt: _createdAt, ...rest }) => rest)
    .sort((a, b) => a.competencyName.localeCompare(b.competencyName));
}

function completionFromScores(
  scores: Array<{ answeredQuestionCount: number; totalQuestionCount: number }>
): number | null {
  const answered = scores.reduce((sum, score) => sum + score.answeredQuestionCount, 0);
  const total = scores.reduce((sum, score) => sum + score.totalQuestionCount, 0);
  return indexCompletionPercent(answered, total);
}

function goalChangeDays(goals: CareerPlanGoal[]): Set<string> {
  const days = new Set<string>();
  for (const goal of goals) {
    // Day keys come from create/archive only. In-place edits bump updatedAt but keep
    // live field values, so updatedAt cannot reconstruct point-in-time hub rows.
    for (const iso of [goal.createdAt, goal.archivedAt]) {
      const key = toUtcDateKey(iso);
      if (key) days.add(key);
    }
  }
  return days;
}

function snapshotChangeDays(snapshots: CareerPlanScoreSnapshot[]): Set<string> {
  const days = new Set<string>();
  for (const snapshot of snapshots) {
    const key = toUtcDateKey(snapshot.createdAt);
    if (key) days.add(key);
  }
  return days;
}

export function buildCareerPlanHistoryRows({
  goals,
  assessment,
  includeExperienceIndexHistory = false
}: {
  goals: CareerPlanGoal[];
  assessment?: CareerPlanAssessment | null;
  includeExperienceIndexHistory?: boolean;
}): CareerPlanHistoryRow[] {
  const hasGoals = goals.length > 0;
  const hasAssessment = !!assessment;
  if (!hasGoals && !hasAssessment) {
    return [];
  }

  const snapshots = assessment?.competencyScoreSnapshots ?? [];
  const goalDays = goalChangeDays(goals);
  const eiDays = includeExperienceIndexHistory ? snapshotChangeDays(snapshots) : new Set<string>();
  const allDays = new Set([...goalDays, ...eiDays]);

  const historicalRows: CareerPlanHistoryRow[] = [...allDays]
    .sort((a, b) => b.localeCompare(a))
    .map((dayKey) => {
      const asOfGoals = goalsAsOfDay(goals, dayKey);
      const goal = primaryGoal(asOfGoals);
      const itemsChanged: string[] = [];
      if (goalDays.has(dayKey)) itemsChanged.push(ITEM_CAREER_GOALS);
      if (eiDays.has(dayKey)) itemsChanged.push(ITEM_EXPERIENCE_INDEX);

      const asOfScores = includeExperienceIndexHistory ? eiScoresAsOfDay(snapshots, dayKey) : [];
      const lastUpdatedCandidates = [
        ...goals
          .flatMap((g) => [g.createdAt, g.archivedAt])
          .filter((iso): iso is string => !!iso && toUtcDateKey(iso) === dayKey),
        ...snapshots.filter((s) => toUtcDateKey(s.createdAt) === dayKey).map((s) => s.createdAt)
      ];
      const lastUpdated = lastUpdatedCandidates.sort((a, b) => b.localeCompare(a))[0] || `${dayKey}T00:00:00.000Z`;

      return {
        id: dayKey,
        dayKey,
        isLatest: false,
        itemsChanged,
        itemsChangedLabel: itemsChanged.join(", "),
        nextRole: getNextRoleLabel(goal),
        timeframe: formatGoalTimeframe(goal),
        clarity: formatClarity(goal?.clarity),
        indexCompletionPercent: includeExperienceIndexHistory ? completionFromScores(asOfScores) : null,
        lastUpdated,
        goals: asOfGoals
      };
    });

  const latestGoals = goalsAsOfDay(goals, LATEST_DAY_KEY);
  const latestGoal = primaryGoal(latestGoals);
  const latestItems: string[] = [];
  if (hasGoals) latestItems.push(ITEM_CAREER_GOALS);
  if (hasAssessment) latestItems.push(ITEM_EXPERIENCE_INDEX);

  const latestRow: CareerPlanHistoryRow = {
    id: LATEST_DAY_KEY,
    dayKey: LATEST_DAY_KEY,
    isLatest: true,
    itemsChanged: latestItems,
    itemsChangedLabel: latestItems.join(", ") || "—",
    nextRole: getNextRoleLabel(latestGoal),
    timeframe: formatGoalTimeframe(latestGoal),
    clarity: formatClarity(latestGoal?.clarity),
    indexCompletionPercent: assessment
      ? indexCompletionPercent(assessment.answeredQuestionCount, assessment.totalQuestionCount)
      : null,
    lastUpdated:
      [latestGoal?.updatedAt, assessment?.updatedAt]
        .filter(Boolean)
        .sort((a, b) => String(b).localeCompare(String(a)))[0] || new Date().toISOString(),
    goals: latestGoals
  };

  return [latestRow, ...historicalRows];
}
